import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Edit2, Trash2, Plus, ChevronDown, ChevronRight, Ticket, RefreshCw, Calendar } from 'lucide-react';
import posEventsApi from 'api/requests/posEvents';
import menuApi from 'api/requests/menu';
import eventsApi from 'api/requests/events';
import BottomSheet from 'components/_UI/BottomSheet';
import TicketForm from '../forms/TicketForm';
import ConfirmDialog from '../components/ConfirmDialog';
import colors from 'theme/colors';

export default forwardRef(function PosEventsTab({ slug, toast }, ref) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [eventSheet, setEventSheet] = useState({ open: false, data: null, saving: false, error: null });
    const [eventName, setEventName] = useState('');
    const [eventIsActive, setEventIsActive] = useState(true);
    const [eventLinkedId, setEventLinkedId] = useState('');
    const [selectedOnlineEvent, setSelectedOnlineEvent] = useState(null);
    const [ticketSheet, setTicketSheet] = useState({ open: false, posEventId: null, data: null });
    const [confirmEvent, setConfirmEvent] = useState({ open: false, id: null, name: '' });
    const [confirmTicket, setConfirmTicket] = useState({ open: false, id: null, name: '' });
    const [deletingEvent, setDeletingEvent] = useState(false);
    const [deletingTicket, setDeletingTicket] = useState(false);
    const [togglingEventId, setTogglingEventId] = useState(null);
    const [ticketsFetchedIds, setTicketsFetchedIds] = useState(new Set());
    const [loadingTicketsId, setLoadingTicketsId] = useState(null);
    const [onlineEvents, setOnlineEvents] = useState([]);
    const [onlineEventsLoading, setOnlineEventsLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        openCreate() { openEventSheet(null); }
    }));

    useEffect(() => { fetchEvents(); }, []);

    async function fetchEvents() {
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await posEventsApi.list(slug);
            setEvents(Array.isArray(res) ? res : (res.data || []));
        } catch {
            setError('Não foi possível carregar os eventos.');
        } finally {
            setLoading(false);
        }
    }

    function openEventSheet(ev) {
        setEventName(ev?.name || '');
        setEventIsActive(ev?.is_active ?? true);
        setEventLinkedId(ev?.event_id ? String(ev.event_id) : '');
        setSelectedOnlineEvent(null);
        setEventSheet({ open: true, data: ev, saving: false, error: null });
        setOnlineEvents([]);
        setOnlineEventsLoading(true);
        eventsApi.myEvents()
            .then(({ data }) => {
                const now = data?.data?.now?.items?.data || [];
                const next = data?.data?.next?.items?.data || [];
                setOnlineEvents([
                    ...now.map(e => ({ ...e, _section: 'now' })),
                    ...next.map(e => ({ ...e, _section: 'next' })),
                ]);
            })
            .catch(() => {})
            .finally(() => setOnlineEventsLoading(false));
    }

    function closeEventSheet() {
        setEventSheet({ open: false, data: null, saving: false, error: null });
    }

    async function handleSaveEvent() {
        if (!eventName.trim()) {
            setEventSheet(prev => ({ ...prev, error: 'Nome é obrigatório.' }));
            return;
        }
        setEventSheet(prev => ({ ...prev, saving: true, error: null }));
        const isEditing = !!eventSheet.data;
        const payload = {
            name: eventName.trim(),
            is_active: eventIsActive,
            ...(!isEditing && { event_slug: selectedOnlineEvent?.slug ?? null }),
        };
        try {
            let saved;
            if (eventSheet.data) {
                const { data: res } = await posEventsApi.update(slug, eventSheet.data.id, payload);
                saved = res.data ?? res;
                setEvents(prev => prev.map(e => e.id === saved.id ? saved : e));
                toast?.success('Evento atualizado com sucesso');
            } else {
                const { data: res } = await posEventsApi.create(slug, payload);
                saved = res.data ?? res;
                setEvents(prev => [...prev, saved]);
                toast?.success('Evento criado com sucesso');
            }
            closeEventSheet();
        } catch {
            setEventSheet(prev => ({ ...prev, saving: false, error: 'Erro ao salvar evento. Tente novamente.' }));
        }
    }

    async function toggleExpand(id) {
        const willExpand = !expandedIds.has(id);
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
        if (willExpand && !ticketsFetchedIds.has(id)) {
            setLoadingTicketsId(id);
            try {
                const { data } = await posEventsApi.listTickets(slug, id);
                const tickets = Array.isArray(data) ? data : (data.data || []);
                setEvents(prev => prev.map(ev => ev.id === id ? { ...ev, tickets } : ev));
                setTicketsFetchedIds(prev => new Set(prev).add(id));
            } catch {
                toast?.error('Erro ao carregar ingressos');
            } finally {
                setLoadingTicketsId(null);
            }
        }
    }

    function requestDeleteEvent(ev) {
        setConfirmEvent({ open: true, id: ev.id, name: ev.name });
    }

    async function handleConfirmDeleteEvent() {
        setDeletingEvent(true);
        try {
            await posEventsApi.remove(slug, confirmEvent.id);
            setEvents(prev => prev.filter(e => e.id !== confirmEvent.id));
            toast?.success('Evento removido com sucesso');
        } catch {
            toast?.error('Erro ao remover evento');
        } finally {
            setDeletingEvent(false);
            setConfirmEvent({ open: false, id: null, name: '' });
        }
    }

    function requestDeleteTicket(ticket) {
        setConfirmTicket({ open: true, id: ticket.id, name: ticket.name });
    }

    async function handleConfirmDeleteTicket() {
        setDeletingTicket(true);
        try {
            // Tickets are menu products — use menuApi.deleteProduct
            await menuApi.deleteProduct(slug, confirmTicket.id);
            setEvents(prev => prev.map(ev => ({
                ...ev,
                tickets: (ev.tickets || []).filter(t => t.id !== confirmTicket.id),
            })));
            toast?.success('Ingresso removido com sucesso');
        } catch {
            toast?.error('Erro ao remover ingresso');
        } finally {
            setDeletingTicket(false);
            setConfirmTicket({ open: false, id: null, name: '' });
        }
    }

    async function handleToggleEvent(ev) {
        setTogglingEventId(ev.id);
        try {
            await posEventsApi.update(slug, ev.id, { is_active: !ev.is_active });
            setEvents(prev => prev.map(e =>
                e.id === ev.id ? { ...e, is_active: !e.is_active } : e
            ));
        } catch {
            toast?.error('Erro ao atualizar evento');
        } finally {
            setTogglingEventId(null);
        }
    }

    function handleTicketSaved(saved, posEventId) {
        const isEdit = !!ticketSheet.data;
        setEvents(prev => prev.map(ev => {
            if (ev.id !== posEventId) return ev;
            const tickets = ev.tickets || [];
            const exists = tickets.find(t => t.id === saved.id);
            return {
                ...ev,
                tickets: exists
                    ? tickets.map(t => t.id === saved.id ? saved : t)
                    : [...tickets, saved],
            };
        }));
        toast?.success(isEdit ? 'Ingresso atualizado' : 'Ingresso criado com sucesso');
        setTicketSheet({ open: false, posEventId: null, data: null });
    }

    if (loading) {
        return (
            <LoadingMsg>
                <Spinner />
                Carregando eventos...
            </LoadingMsg>
        );
    }

    if (error) {
        return (
            <ErrorMsg>
                {error}
                <RefreshBtn onClick={fetchEvents} title="Tentar novamente">
                    <RefreshCw size={14} /> Tentar novamente
                </RefreshBtn>
            </ErrorMsg>
        );
    }

    return (
        <Container>
            {events.length === 0 && (
                <EmptyMsg>
                    <Ticket size={32} color={colors.occasionalPurple} style={{ marginBottom: 10 }} />
                    <strong>Nenhum evento criado</strong>
                    <span>Eventos agrupam ingressos para venda no PDV</span>
                    <AddTicketBtn
                        style={{ marginTop: 12, fontSize: 14 }}
                        onClick={() => openEventSheet(null)}
                    >
                        <Plus size={16} /> Criar primeiro evento
                    </AddTicketBtn>
                </EmptyMsg>
            )}

            {events.map(ev => {
                const isExpanded = expandedIds.has(ev.id);
                const tickets = ev.tickets || [];
                return (
                    <EventRow key={ev.id}>
                        <EventHeader onClick={() => toggleExpand(ev.id)}>
                            <EventHeaderLeft>
                                {isExpanded
                                    ? <ChevronDown size={18} color={colors.night} />
                                    : <ChevronRight size={18} color={colors.night} />
                                }
                                <EventHeaderContent>
                                    <EventHeaderTop>
                                        <EventName>{ev.name}</EventName>
                                        <ActiveBadge $active={ev.is_active}>
                                            {ev.is_active ? 'Ativo' : 'Inativo'}
                                        </ActiveBadge>
                                        {ev.linked_event?.name && (
                                            <OnlineBadge>Evento associado</OnlineBadge>
                                        )}
                                    </EventHeaderTop>
                                    {ev.linked_event?.name && (
                                        <LinkedEventName>{ev.linked_event.name}</LinkedEventName>
                                    )}
                                </EventHeaderContent>
                            </EventHeaderLeft>
                            <EventActions onClick={e => e.stopPropagation()}>
                                <TicketSwitch
                                    $active={ev.is_active}
                                    $loading={togglingEventId === ev.id}
                                    onClick={() => !togglingEventId && handleToggleEvent(ev)}
                                    title={ev.is_active ? 'Desativar evento' : 'Ativar evento'}
                                >
                                    <TicketSwitchKnob $active={ev.is_active} />
                                </TicketSwitch>
                                <IconBtn
                                    onClick={() => openEventSheet(ev)}
                                    aria-label={`Editar evento ${ev.name}`}
                                    title="Editar evento"
                                >
                                    <Edit2 size={15} color={colors.night} />
                                </IconBtn>
                                <IconBtn
                                    $danger
                                    onClick={() => requestDeleteEvent(ev)}
                                    aria-label={`Remover evento ${ev.name}`}
                                    title="Remover evento"
                                >
                                    <Trash2 size={15} color={colors.danger} />
                                </IconBtn>
                            </EventActions>
                        </EventHeader>

                        {isExpanded && (
                            <TicketList>
                                {loadingTicketsId === ev.id ? (
                                    <TicketEmpty>Carregando ingressos...</TicketEmpty>
                                ) : tickets.length === 0 ? (
                                    <TicketEmpty>Nenhum ingresso cadastrado</TicketEmpty>
                                ) : null}
                                {loadingTicketsId !== ev.id && tickets.map(ticket => (
                                    <TicketRow key={ticket.id}>
                                        <Ticket size={14} color={colors.nightLight} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <TicketInfo>
                                            <TicketName>{ticket.name}</TicketName>
                                            {ticket.description && (
                                                <TicketDesc>{ticket.description}</TicketDesc>
                                            )}
                                        </TicketInfo>
                                        {ticket.base_price != null && (
                                            <TicketPrice>
                                                {Number(ticket.base_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            </TicketPrice>
                                        )}
                                        <IconBtn
                                            onClick={() => setTicketSheet({ open: true, posEventId: ev.id, data: ticket })}
                                            aria-label={`Editar ingresso ${ticket.name}`}
                                            title="Editar ingresso"
                                        >
                                            <Edit2 size={14} color={colors.night} />
                                        </IconBtn>
                                        <IconBtn
                                            $danger
                                            onClick={() => requestDeleteTicket(ticket)}
                                            aria-label={`Remover ingresso ${ticket.name}`}
                                            title="Remover ingresso"
                                        >
                                            <Trash2 size={14} color={colors.danger} />
                                        </IconBtn>
                                    </TicketRow>
                                ))}
                                {loadingTicketsId !== ev.id && (
                                    <AddTicketBtn onClick={() => setTicketSheet({ open: true, posEventId: ev.id, data: null })}>
                                        <Plus size={14} /> Novo ingresso
                                    </AddTicketBtn>
                                )}
                            </TicketList>
                        )}
                    </EventRow>
                );
            })}

            {/* PosEvent form sheet */}
            <BottomSheet
                isOpen={eventSheet.open}
                onClose={closeEventSheet}
                title={eventSheet.data ? 'Editar evento' : 'Novo evento'}
            >
                <SheetForm>
                    <FormField>
                        <FormLabel>Nome do evento</FormLabel>
                        <FormInput
                            placeholder="Ex: Festival de Verão"
                            value={eventName}
                            autoFocus
                            onChange={e => setEventName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !eventSheet.saving && handleSaveEvent()}
                        />
                    </FormField>

                    {!eventSheet.data && <FormField>
                        <FormLabel>
                            Evento online vinculado <FormOptional>(opcional)</FormOptional>
                        </FormLabel>
                        <OnlineEventPicker>
                            {onlineEventsLoading ? (
                                <PickerMsg><Spinner $small /> Carregando eventos...</PickerMsg>
                            ) : onlineEvents.length === 0 ? (
                                <PickerEmpty>
                                    <Calendar size={20} color={colors.nightLight} />
                                    <span>Nenhum evento ativo ou futuro encontrado</span>
                                </PickerEmpty>
                            ) : (
                                onlineEvents.map((ev, idx) => {
                                    const reactKey = `${ev._section}-${ev.id ?? ev.slug ?? idx}`;
                                    const isSelected = selectedOnlineEvent === ev;
                                    return (
                                        <OnlineEventCard
                                            key={reactKey}
                                            $selected={isSelected}
                                            onClick={() => setSelectedOnlineEvent(isSelected ? null : ev)}
                                        >
                                            <OnlineEventInfo>
                                                <OnlineEventName>{ev.name}</OnlineEventName>
                                                <OnlineEventBadge $now={ev._section === 'now'}>
                                                    {ev._section === 'now' ? 'Acontecendo agora' : 'Em breve'}
                                                </OnlineEventBadge>
                                            </OnlineEventInfo>
                                            <RadioIndicator $selected={isSelected}>
                                                {isSelected && <RadioDot />}
                                            </RadioIndicator>
                                        </OnlineEventCard>
                                    );
                                })
                            )}
                        </OnlineEventPicker>
                        <FormHint>Vincula este evento PDV a um evento publicado na plataforma.</FormHint>
                    </FormField>}

                    <ToggleRow onClick={() => setEventIsActive(p => !p)}>
                        <ToggleLabel>
                            <ToggleTitle>Ativo</ToggleTitle>
                            <ToggleHint>Visível no PDV ao garçom</ToggleHint>
                        </ToggleLabel>
                        <Switch $active={eventIsActive}>
                            <SwitchKnob $active={eventIsActive} />
                        </Switch>
                    </ToggleRow>

                    {eventSheet.error && <ErrorText>{eventSheet.error}</ErrorText>}

                    <SubmitBtn onClick={handleSaveEvent} disabled={eventSheet.saving}>
                        {eventSheet.saving ? 'Salvando...' : (eventSheet.data ? 'Salvar alterações' : 'Criar evento')}
                    </SubmitBtn>
                </SheetForm>
            </BottomSheet>

            {/* Ticket form sheet */}
            <BottomSheet
                isOpen={ticketSheet.open}
                onClose={() => setTicketSheet({ open: false, posEventId: null, data: null })}
                title={ticketSheet.data ? 'Editar ingresso' : 'Novo ingresso'}
            >
                <TicketForm
                    slug={slug}
                    posEventId={ticketSheet.posEventId}
                    initialData={ticketSheet.data}
                    onSaved={saved => handleTicketSaved(saved, ticketSheet.posEventId)}
                    onCancel={() => setTicketSheet({ open: false, posEventId: null, data: null })}
                />
            </BottomSheet>

            <ConfirmDialog
                isOpen={confirmEvent.open}
                title="Remover evento"
                message={`Tem certeza que deseja remover "${confirmEvent.name}"? Esta ação não pode ser desfeita.`}
                confirmLabel="Remover"
                danger
                loading={deletingEvent}
                onConfirm={handleConfirmDeleteEvent}
                onCancel={() => setConfirmEvent({ open: false, id: null, name: '' })}
            />

            <ConfirmDialog
                isOpen={confirmTicket.open}
                title="Remover ingresso"
                message={`Tem certeza que deseja remover "${confirmTicket.name}"? Esta ação não pode ser desfeita.`}
                confirmLabel="Remover"
                danger
                loading={deletingTicket}
                onConfirm={handleConfirmDeleteTicket}
                onCancel={() => setConfirmTicket({ open: false, id: null, name: '' })}
            />
        </Container>
    );
});

/* ─── Styled Components ─── */

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
    width: ${({ $small }) => $small ? '14px' : '28px'};
    height: ${({ $small }) => $small ? '14px' : '28px'};
    border: ${({ $small }) => $small ? '2px' : '3px'} solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    flex-shrink: 0;
`;

const Container = styled.div`
    padding: 20px 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 100%;
`;

const EventRow = styled.div`
    background: #fff;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const EventHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    &:hover { background: #f5f5f7; }
    transition: background 0.15s;
`;

const EventHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
`;

const EventHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

const EventHeaderTop = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const EventName = styled.span`
    font-weight: 700;
    font-size: 15px;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ActiveBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 8px;
    flex-shrink: 0;
    background: ${({ $active }) => $active ? '#12b76a18' : colors.neutralDark};
    color: ${({ $active }) => $active ? '#12b76a' : colors.nightLight};
`;

const OnlineBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 8px;
    flex-shrink: 0;
    background: ${colors.occasionalPurple}18;
    color: ${colors.occasionalPurple};
`;

const LinkedEventName = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EventActions = styled.div`
    display: flex;
    gap: 4px;
    flex-shrink: 0;
`;

const IconBtn = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;

const TicketList = styled.div`
    padding: 8px 16px 12px 40px;
    border-top: 1px solid ${colors.neutralDark};
    background: #f9f9fb;
`;

const TicketRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px solid ${colors.neutralDark};
    &:last-child { border-bottom: none; }
`;

const TicketInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const TicketEmpty = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    padding: 10px 0;
    margin: 0;
`;

const TicketName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TicketDesc = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TicketSwitch = styled.div`
    width: 34px;
    height: 20px;
    border-radius: 10px;
    background: ${({ $active }) => $active ? colors.occasionalGreen : colors.neutralDark};
    padding: 2px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    cursor: ${({ $loading }) => $loading ? 'wait' : 'pointer'};
    opacity: ${({ $loading }) => $loading ? 0.6 : 1};
    transition: background 0.2s, opacity 0.15s;
`;

const TicketSwitchKnob = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    margin-left: ${({ $active }) => $active ? 'auto' : '0'};
    transition: margin-left 0.2s;
`;

const TicketPrice = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    flex-shrink: 0;
`;

const AddTicketBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${colors.occasionalPurple};
    font-size: 13px;
    font-weight: 600;
    margin-top: 4px;
    transition: opacity 0.15s;
    &:hover { opacity: 0.8; }
`;

const SheetForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FormLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FormOptional = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: ${colors.nightLight};
`;

const FormInput = styled.input`
    height: 44px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 0 14px;
    font-size: 15px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: ${colors.nightLight}; }
`;

const FormHint = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const OnlineEventPicker = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 200px;
    overflow-y: auto;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 6px;
`;

const OnlineEventCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1.5px solid ${({ $selected }) => $selected ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $selected }) => $selected ? `${colors.occasionalPurple}0D` : '#fff'};
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    &:hover { border-color: ${({ $selected }) => $selected ? colors.occasionalPurple : colors.nightLight}; }
`;

const OnlineEventInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

const OnlineEventName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const OnlineEventBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${({ $now }) => $now ? '#12b76a' : colors.occasionalPurple};
    background: ${({ $now }) => $now ? '#12b76a18' : `${colors.occasionalPurple}14`};
    padding: 1px 7px;
    border-radius: 6px;
    align-self: flex-start;
`;

const PickerMsg = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 13px;
    color: ${colors.nightLight};
`;

const RadioIndicator = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 2px solid ${({ $selected }) => $selected ? colors.occasionalPurple : colors.nightLight};
    background: ${({ $selected }) => $selected ? colors.occasionalPurple : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;
`;

const RadioDot = styled.div`
    width: 10px;
    height: 6px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(-45deg) translateY(-1px);
`;

const PickerEmpty = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 16px 12px;
    font-size: 13px;
    color: ${colors.nightLight};
    text-align: center;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #f5f5f7;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: #ededf0; }
`;

const ToggleLabel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ToggleTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ToggleHint = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const Switch = styled.div`
    width: 48px;
    height: 28px;
    border-radius: 14px;
    background: ${({ $active }) => $active ? colors.occasionalGreen : colors.neutralDark};
    padding: 2px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: background 0.2s;
`;

const SwitchKnob = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    margin-left: ${({ $active }) => $active ? 'auto' : '0'};
    transition: margin-left 0.2s;
`;

const ErrorText = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    margin: 0;
`;

const SubmitBtn = styled.button`
    width: 100%;
    height: 48px;
    border-radius: 12px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LoadingMsg = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 32px 0;
    text-align: center;
    font-size: 14px;
    color: ${colors.nightLight};
`;

const ErrorMsg = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 32px 0;
    text-align: center;
    font-size: 14px;
    color: ${colors.nightLight};
`;

const EmptyMsg = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    text-align: center;
    font-size: 14px;
    color: ${colors.nightLight};
    gap: 4px;
    & strong { font-size: 16px; font-weight: 700; color: ${colors.nightDark}; }
`;

const RefreshBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    cursor: pointer;
    margin-top: 8px;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;
